import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { StageBadge, stageLabel, fmtAmount, fmtDate, fmtBillingPeriod, fmtRemaining } from "@/lib/util";
import CurrencyIcon from "@/components/CurrencyIcon";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Copy, ThumbsUp, XCircle, WhatsappLogo, CurrencyBtc } from "@phosphor-icons/react";
import { toast } from "sonner";
import WhatsAppExport from "@/components/WhatsAppExport";
import InlineTxHashDialog from "@/components/InlineTxHashDialog";

const STAGES = ["", "pending_approval", "pending_audit", "approved", "paid", "verified", "rejected", "draft"];

export default function Queue() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [params, setParams] = useSearchParams();
  const stage = params.get("stage") || "";
  const brand = params.get("brand") || "";
  const currency = params.get("currency") || "";
  const vendor = params.get("vendor") || "";
  const expenseType = params.get("expense_type") || "";
  const mode = params.get("mode") || "";
  const stageIn = params.get("stage_in") || "";   // comma-list — drill-through from Reports
  const basis = params.get("basis") || "";        // "paid" | "accounting" | ""
  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";
  const mine = params.get("mine") === "1" || params.get("mine") === "true";
  const [q, setQ] = useState(params.get("q") || "");
  // Bulk accounting-date selection state
  const [selected, setSelected] = useState(new Set());
  const [waOpen, setWaOpen] = useState(false);
  const [txPayment, setTxPayment] = useState(null);   // opens the inline "Add tx hash" dialog for this row
  const [bulkDate, setBulkDate] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleRow = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((s) => (
    s.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((r) => r.id))
  ));
  // Build /payments query params from current URL state. Centralized so all
  // fetch callers (initial load, refetch, bulk-refetch) stay in sync.
  const buildParams = () => {
    const p = {};
    if (stage) p.stage = stage;
    if (stageIn) p.stage_in = stageIn;                           // drill-through multi-stage
    if (basis) p.basis = basis;                                   // date field selector
    if (brand) p.brand_id = brand;
    if (currency) p.currency = currency;
    if (vendor) p.vendor = vendor;
    if (expenseType) p.expense_type = expenseType;
    if (mode) p.payment_mode = mode;
    if (dateFrom) p.date_from = dateFrom;                        // YYYY-MM-DD
    if (dateTo) p.date_to = `${dateTo}T23:59:59`;                // inclusive end
    if (q) p.q = q;
    if (mine) p.mine = true;
    return p;
  };

  const applyBulk = async () => {
    if (!bulkDate || selected.size === 0) { toast.error("Pick a date and at least one row"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post("/payments/bulk-set-accounting-date", {
        ids: Array.from(selected), accounting_date: bulkDate,
      });
      toast.success(`Stamped ${r.data.modified} of ${selected.size} invoices with ${bulkDate}`);
      setSelected(new Set()); setBulkDate("");
      const rows2 = await api.get("/payments", { params: buildParams() });
      setRows(rows2.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };
  const clearBulk = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Clear accounting date on ${selected.size} invoice(s)?`)) return;
    setBulkBusy(true);
    try {
      await api.post("/payments/bulk-set-accounting-date", { ids: Array.from(selected), accounting_date: null });
      toast.success("Cleared");
      setSelected(new Set());
      const rows2 = await api.get("/payments", { params: buildParams() });
      setRows(rows2.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  // Refetch current query — used after approve/reject actions
  const refetch = async () => {
    const r = await api.get("/payments", { params: buildParams() });
    setRows(r.data);
  };

  // Bulk actions — three flavours depending on the current stage filter:
  //   • pending_approval → approver approve/reject (chain / pre-audit)
  //   • pending_audit    → auditor approve/reject
  //   • paid             → verify (payer OR owning requester)
  const canApprove = user?.is_approver || user?.role === "admin" || user?.role === "approver";
  const canAudit   = user?.role === "auditor" || user?.role === "admin";
  const canVerify  = user?.role === "payer"   || user?.role === "admin" || user?.role === "requester";
  const showApprovalBulk = stage === "pending_approval" && canApprove;
  const showAuditBulk    = stage === "pending_audit"    && canAudit;
  const showVerifyBulk   = stage === "paid"             && canVerify;
  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Approve ${selected.size} request${selected.size === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const endpoint = showAuditBulk ? "/payments/bulk-approve" : "/payments/bulk-approver-approve";
      const { data } = await api.post(endpoint, { ids: [...selected] });
      const skippedNonAssigned = (data.skipped || []).filter((s) => s.reason === "not_assigned").length;
      if (data.approved_count > 0) toast.success(`Approved ${data.approved_count}.`);
      if (skippedNonAssigned > 0) toast.warning(`${skippedNonAssigned} skipped — not assigned to you.`);
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Bulk approve failed"); }
    finally { setBulkBusy(false); }
  };
  const bulkReject = async () => {
    if (selected.size === 0) return;
    const reason = window.prompt(`Reject ${selected.size} request${selected.size === 1 ? "" : "s"}? Enter a shared reason:`);
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A rejection reason is required."); return; }
    setBulkBusy(true);
    try {
      const endpoint = showAuditBulk ? "/payments/bulk-reject" : "/payments/bulk-approver-reject";
      const { data } = await api.post(endpoint, { ids: [...selected], reason: trimmed });
      const skippedNonAssigned = (data.skipped || []).filter((s) => s.reason === "not_assigned").length;
      if (data.rejected_count > 0) toast.success(`Rejected ${data.rejected_count}.`);
      if (skippedNonAssigned > 0) toast.warning(`${skippedNonAssigned} skipped — not assigned to you.`);
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Bulk reject failed"); }
    finally { setBulkBusy(false); }
  };
  const bulkVerify = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Verify ${selected.size} paid payment${selected.size === 1 ? "" : "s"}? Only do this once you've confirmed the money landed.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/payments/bulk-verify", { ids: [...selected], note: "Bulk verified" });
      if (data.verified_count > 0) toast.success(`Verified ${data.verified_count}.`);
      const skipped = data.skipped?.length || 0;
      if (skipped > 0) toast.warning(`${skipped} skipped (wrong stage or not yours).`);
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Bulk verify failed"); }
    finally { setBulkBusy(false); }
  };

  // Admin-only: move ANY selection to ANY stage (forward or backward).
  // Backward moves clear paid_by/tx_hash/verified_by fields on those rows so
  // the payment is truly re-actionable at the target.
  const canBulkSetStage = user?.role === "admin";
  const bulkSetStage = async (targetStage) => {
    if (selected.size === 0 || !targetStage) return;
    const isBackward = ["draft","pending_approval","pending_audit"].includes(targetStage);
    const warn = isBackward
      ? `⚠ Move ${selected.size} request(s) BACK to "${targetStage}"?\n\nThis will clear the fields owned by any stages walked past (paid_by, tx_hash, blockchain, verified_by, etc). Continue?`
      : `Move ${selected.size} request(s) to "${targetStage}"?`;
    if (!window.confirm(warn)) return;
    const reason = window.prompt(`Reason (required):`, isBackward ? "Reverted for correction" : "Manual stage change");
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A reason is required."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/payments/bulk-set-stage", {
        ids: [...selected], target_stage: targetStage, reason: trimmed,
      });
      if (data.changed > 0) toast.success(`Moved ${data.changed} to ${targetStage}.`);
      const skippedCount = data.skipped?.length || 0;
      if (skippedCount > 0) toast.warning(`${skippedCount} skipped (already there or rejected).`);
      setSelected(new Set());
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Bulk stage change failed"); }
    finally { setBulkBusy(false); }
  };

  // Swipe-triggered single actions on mobile cards
  const swipeApprove = async (p) => {
    try {
      await api.post(`/payments/${p.id}/approver-approve`, { note: "Approved via swipe" });
      toast.success(`Approved ${p.reference}`);
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Approve failed"); }
  };
  const swipeReject = async (p) => {
    const reason = window.prompt(`Reject ${p.reference}? Enter a reason:`);
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A reason is required."); return; }
    try {
      await api.post(`/payments/${p.id}/approver-reject`, { note: trimmed });
      toast.success(`Rejected ${p.reference}`);
      await refetch();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Reject failed"); }
  };

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data.filter((b) => b.is_active)));
    api.get("/currencies").then((r) => setCurrencies(r.data.filter((c) => c.is_active)));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/payments/expense-types").then((r) => setExpenseTypes(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/payments", { params: buildParams() }).then((r) => setRows(r.data)).catch(() => {});
  }, [stage, stageIn, basis, brand, currency, vendor, expenseType, mode, dateFrom, dateTo, q, mine]);

  const setFilter = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const hasFilters = stage || stageIn || basis || brand || currency || vendor || expenseType || mode || dateFrom || dateTo || q;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1700px]">
      <header className="mb-6">
        <div className="overline">Queue</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">
          <span className="text-gold-gradient">Requests</span>
        </h1>
      </header>

      <div className="glass rounded-2xl p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="queue-search" placeholder="Reference, vendor, amount, tx hash, wallet…"
                value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl bg-white/70 border-white/60 h-10" />
            </div>
          </div>
          <div className="w-40">
            <Select value={stage || "all"} onValueChange={(v) => setFilter("stage", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="queue-stage" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue placeholder="Stage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {STAGES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{stageLabel[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Select value={brand || "all"} onValueChange={(v) => setFilter("brand", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="queue-brand" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={currency || "all"} onValueChange={(v) => setFilter("currency", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="queue-currency" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue placeholder="Currency" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All currencies</SelectItem>
                {currencies.map((c) => (
                  <SelectItem key={c.id} value={c.code}>
                    <span className="inline-flex items-center gap-2"><CurrencyIcon code={c.code} size={14} /> {c.code}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-56">
            <Select value={vendor || "all"} onValueChange={(v) => setFilter("vendor", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="queue-vendor" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue placeholder="Vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {vendors.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Select value={mode || "all"} onValueChange={(v) => setFilter("mode", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="queue-mode" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue placeholder="Mode" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="crypto">Crypto</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="credit_card">Credit card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-52">
            <Select value={expenseType || "all"} onValueChange={(v) => setFilter("expense_type", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="queue-expense-type" className="rounded-xl bg-white/70 border-white/60 h-10"><SelectValue placeholder="Expense type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All expense types</SelectItem>
                {expenseTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold pl-1">Raised from</label>
            <Input
              type="date"
              data-testid="queue-date-from"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setFilter("date_from", e.target.value)}
              className="rounded-xl bg-white/70 border-white/60 h-10 w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold pl-1">Raised to</label>
            <Input
              type="date"
              data-testid="queue-date-to"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setFilter("date_to", e.target.value)}
              className="rounded-xl bg-white/70 border-white/60 h-10 w-40"
            />
          </div>
          {hasFilters && (
            <button data-testid="queue-clear" onClick={clearAll} className="btn-ghost rounded-xl h-10 px-4 text-sm">Clear</button>
          )}
        </div>
        {(stageIn || basis) && (
          <div data-testid="queue-drill-badge" className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-900 border border-emerald-300">
            Drilled from Reports · {stageIn ? `stages: ${stageIn.replace(",", " + ")}` : ""}{stageIn && basis ? " · " : ""}{basis ? `dates by ${basis === "paid" ? "paid date" : basis === "accounting" ? "accounting date" : basis}` : ""}
            <button
              onClick={() => {
                const n = new URLSearchParams(params);
                n.delete("stage_in"); n.delete("basis");
                setParams(n);
              }}
              data-testid="queue-drill-off"
              className="text-emerald-700 underline hover:text-emerald-900"
            >Clear drill</button>
          </div>
        )}
        {mine && (
          <div data-testid="queue-mine-badge" className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-900 border border-purple-300">
            Showing only items assigned to you{user?.role === "admin" ? " or unassigned" : ""}
            <button
              onClick={() => {
                const n = new URLSearchParams(params);
                n.delete("mine");
                setParams(n);
              }}
              data-testid="queue-mine-off"
              className="text-purple-700 underline hover:text-purple-900"
            >Show all</button>
          </div>
        )}
      </div>

      {/* Global "WhatsApp all visible" button — visible when there are rows
          on screen. Uses the current filter to build the message. */}
      {rows.length > 0 && selected.size === 0 && (
        <div className="mb-3 flex justify-end">
          <button
            data-testid="wa-export-visible"
            onClick={() => setWaOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white/70 border border-emerald-300 text-emerald-800 text-xs font-bold hover:bg-emerald-50"
            title={`Build a WhatsApp message from all ${rows.length} visible payment(s)`}
          >
            <WhatsappLogo size={13} weight="fill" /> WhatsApp msg · {rows.length} visible
          </button>
        </div>
      )}

      {/* Bulk toolbar — only when at least one row is selected */}
      {selected.size > 0 && (
        <div data-testid="bulk-toolbar" className="mb-3 flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50/80 border border-amber-300 p-3">
          <div className="text-sm font-semibold text-amber-900">{selected.size} selected</div>
          <button
            data-testid="bulk-whatsapp"
            onClick={() => setWaOpen(true)}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700"
          >
            <WhatsappLogo size={14} weight="fill" /> WhatsApp msg
          </button>
          {(showApprovalBulk || showAuditBulk) ? (
            <>
              <button
                data-testid="bulk-approve"
                onClick={bulkApprove}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
              >
                <ThumbsUp size={14} weight="fill" /> Approve {selected.size}
              </button>
              <button
                data-testid="bulk-reject"
                onClick={bulkReject}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-red-100 text-red-800 border border-red-300 text-sm font-bold hover:bg-red-200 disabled:opacity-50"
              >
                <XCircle size={14} weight="fill" /> Reject {selected.size}
              </button>
            </>
          ) : showVerifyBulk ? (
            <button
              data-testid="bulk-verify"
              onClick={bulkVerify}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
            >
              <ThumbsUp size={14} weight="fill" /> Verify {selected.size}
            </button>
          ) : (
            <>
              <Input
                data-testid="bulk-accounting-date"
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                className="h-9 w-40 text-sm"
              />
              <button
                data-testid="bulk-accounting-apply"
                onClick={applyBulk}
                disabled={bulkBusy || !bulkDate}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                {bulkBusy ? "Saving…" : "Stamp accounting date"}
              </button>
              <button
                data-testid="bulk-accounting-clear"
                onClick={clearBulk}
                disabled={bulkBusy}
                className="px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 text-sm font-semibold hover:bg-amber-100"
              >
                Clear date
              </button>
            </>
          )}
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-slate-600 underline ml-auto"
          >Deselect all</button>
          {/* Admin-only "Move to…" — arbitrary stage change (fwd or back) */}
          {canBulkSetStage && (
            <div className="w-full flex flex-wrap items-center gap-2 pt-2 mt-1 border-t border-amber-200">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">Admin · Move selected to:</span>
              <Select value="" onValueChange={(v) => v && bulkSetStage(v)}>
                <SelectTrigger data-testid="bulk-move-stage" className="h-9 w-56 bg-white/80 border-amber-300 text-sm">
                  <SelectValue placeholder="Pick a stage…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_approval">⏪ Pending approval</SelectItem>
                  <SelectItem value="pending_audit">⏪ Pending audit</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[10px] text-amber-700 italic">Backward moves wipe artefacts of stages walked past. Fully audited.</span>
            </div>
          )}
        </div>
      )}

      {/* Mobile card list — replaces the wide table on small screens */}
      <div className="md:hidden space-y-3" data-testid="queue-mobile-list">
        {rows.map((p) => (
          <MobileRequestCard
            key={p.id}
            p={p}
            selected={selected.has(p.id)}
            onToggle={() => toggleRow(p.id)}
            canSwipeApprove={showApprovalBulk && (user?.role === "admin" || p.assigned_approver_id === user?.id)}
            onSwipeApprove={swipeApprove}
            onSwipeReject={swipeReject}
          />
        ))}
        {rows.length === 0 && (
          <div className="glass rounded-2xl p-8 text-center text-sm text-slate-500">No matching requests.</div>
        )}
      </div>

      {/* Desktop table — pixel-based widths + horizontal scroll on narrow
          screens so the right-side action column is always reachable, even
          after the multi-brand / tx-hash cells widened the row. */}
      <div className="hidden md:block glass rounded-2xl overflow-x-auto">
        <table className="ledger-table text-sm table-fixed w-full min-w-[1440px]">
          <colgroup>
            <col style={{ width: "36px" }} />        {/* select */}
            <col style={{ width: "110px" }} />       {/* Ref */}
            <col style={{ width: "100px" }} />       {/* Dates */}
            <col style={{ width: "140px" }} />       {/* Brand */}
            <col style={{ width: "170px" }} />       {/* Vendor */}
            <col style={{ width: "110px" }} />       {/* Type (expense_type) */}
            <col style={{ width: "130px" }} />       {/* Amount */}
            <col style={{ width: "80px" }} />        {/* Mode */}
            <col style={{ width: "170px" }} />       {/* Recipient */}
            <col style={{ width: "140px" }} />       {/* People */}
            <col style={{ width: "100px" }} />       {/* Stage */}
            <col style={{ width: "150px" }} />       {/* actions (Add hash + Open) */}
          </colgroup>
          <thead className="bg-white/40 sticky top-0">
            <tr className="text-left">
              <th className="px-2 py-2">
                <input
                  data-testid="bulk-select-all"
                  type="checkbox"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={toggleAll}
                  className="accent-amber-600 cursor-pointer"
                />
              </th>
              {["Ref","Dates","Brand","Vendor","Type","Amount","Mode","Recipient","People","Stage",""].map((h,i) => (
                <th key={i} className="px-3 py-2 overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const expiredMs = p.wallet_expires_at ? Date.parse(p.wallet_expires_at) : null;
              const walletExpired = expiredMs && Number.isFinite(expiredMs) && expiredMs < Date.now()
                && ["draft","pending_approval","pending_audit","approved","rejected"].includes(p.stage);
              return (
              <tr key={p.id} data-testid={`queue-row-${p.reference}`} className={`${selected.has(p.id) ? "bg-amber-50/40" : ""} ${walletExpired ? "bg-red-50/60" : ""}`}>
                <td className="px-2 py-3 align-top">
                  <input
                    data-testid={`bulk-select-${p.reference}`}
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleRow(p.id)}
                    className="accent-amber-600 cursor-pointer mt-1"
                  />
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="font-mono text-xs text-amber-700 font-semibold truncate" title={p.reference}>{p.reference}</div>
                  <div className="font-mono text-[10px] text-slate-500 mt-0.5 truncate" title={fmtBillingPeriod(p.billing_month, p.billing_month_end)}>{fmtBillingPeriod(p.billing_month, p.billing_month_end)}</div>
                </td>
                <td className="px-3 py-3 align-top text-xs">
                  <div className="text-slate-700">{fmtDate(p.created_at).split(",")[0]}</div>
                  {p.accounting_date && <div className="font-mono text-[10px] text-slate-500 mt-0.5">Acct · {p.accounting_date}</div>}
                </td>
                <td className="px-3 py-3 align-top">
                  <BrandCell p={p} />
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="text-sm truncate" title={p.vendor_name}>{p.vendor_name}</div>
                </td>
                <td className="px-3 py-3 align-top">
                  {p.expense_type ? (
                    <span data-testid={`queue-expense-${p.reference}`} className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 truncate max-w-full" title={p.expense_type}>
                      {p.expense_type}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex items-center gap-1.5 font-mono font-semibold text-sm">
                    <CurrencyIcon code={p.currency_code} size={14} />
                    <span className="truncate">{fmtAmount(p.net_amount, p.currency_code)}</span>
                  </div>
                </td>
                <td className="px-3 py-3 align-top uppercase text-[11px] text-slate-600 truncate" title={`${p.payment_mode}${p.network ? ` · ${p.network}` : ""}`}>
                  {p.payment_mode}{p.network ? <div className="normal-case text-[10px] text-slate-500">{p.network}</div> : null}
                </td>
                <td className="px-3 py-3 align-top">
                  <RecipientCell p={p} />
                  {walletExpired && (
                    <div data-testid={`queue-wallet-expired-${p.reference}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-black uppercase tracking-wider">
                      ⚠ Expired · {fmtRemaining(p.wallet_expires_at)}
                    </div>
                  )}
                  {!walletExpired && p.wallet_expires_at && (
                    <div data-testid={`queue-wallet-remaining-${p.reference}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 text-[9px] font-bold uppercase tracking-wider" title={`Wallet valid until ${new Date(p.wallet_expires_at).toLocaleString()}`}>
                      ⏳ {fmtRemaining(p.wallet_expires_at)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="space-y-1">
                    <WhoCell name={p.requester_name} at={p.created_at} tint="slate" />
                    {p.audited_by_name && <WhoCell name={p.audited_by_name} at={p.audited_at} tint="sky" />}
                    {p.paid_by_name && <WhoCell name={p.paid_by_name} at={p.paid_at} tint="emerald" />}
                  </div>
                </td>
                <td className="px-3 py-3 align-top"><StageBadge stage={p.stage} approvalStep={p.approval_step} /></td>
                <td className="px-3 py-3 align-top text-right whitespace-nowrap">
                  {p.stage === "approved" && p.payment_mode === "crypto" && (user?.role === "payer" || user?.role === "admin") && (
                    <button
                      data-testid={`queue-tx-hash-${p.reference}`}
                      onClick={() => setTxPayment(p)}
                      className="inline-flex items-center gap-1 mr-2 px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-700"
                      title="Paste tx hash and auto-verify on-chain"
                    >
                      <CurrencyBtc size={11} weight="bold" /> Add hash
                    </button>
                  )}
                  <Link to={`/requests/${p.id}`} className="text-xs font-semibold text-amber-700 hover:underline whitespace-nowrap" data-testid={`queue-open-${p.reference}`}>Open →</Link>
                </td>
              </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-10 text-center text-sm text-slate-500">No matching requests.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs text-slate-500 font-mono">{rows.length} request{rows.length === 1 ? "" : "s"}</div>

      {/* WhatsApp export: uses selection when non-empty, otherwise all filtered rows */}
      <WhatsAppExport
        open={waOpen}
        onOpenChange={setWaOpen}
        rows={selected.size > 0 ? rows.filter((r) => selected.has(r.id)) : rows}
        defaultTitle={stage === "paid" ? "Payments Made" : stage === "verified" ? "Payments Verified" : "Entries Pending"}
      />
      <InlineTxHashDialog
        payment={txPayment}
        onClose={() => setTxPayment(null)}
        onDone={async () => { setTxPayment(null); await refetch(); }}
      />
    </div>
  );
}


// Brand cell — a single-brand pill, or a compact split indicator that shows
// each brand + its share of the payment. On desktop we show up to 2 chips
// stacked; on hover the `title` reveals the full breakdown.
function BrandCell({ p }) {
  const allocs = p.brand_allocations || [];
  const isSplit = allocs.length > 1;
  if (!isSplit) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900 max-w-full truncate" title={p.brand_name}>
        {p.brand_name || "—"}
      </span>
    );
  }
  const breakdown = allocs.map((a) => `${a.brand_name}: ${fmtAmount(a.amount, p.currency_code)}`).join("\n");
  return (
    <div className="space-y-1" title={breakdown}>
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-violet-100 border border-violet-300 text-violet-900">
        {allocs.length}-brand split
      </div>
      {allocs.slice(0, 2).map((a) => (
        <div key={a.brand_id} className="flex items-center gap-1 text-[10px]">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900 truncate max-w-[80px]" title={a.brand_name}>{a.brand_name}</span>
          <span className="font-mono text-slate-700">{fmtAmount(a.amount, p.currency_code)}</span>
        </div>
      ))}
      {allocs.length > 2 && (
        <div className="text-[9px] text-slate-500 font-mono">+{allocs.length - 2} more…</div>
      )}
    </div>
  );
}

function WhoCell({ name, at, tint }) {
  if (!name) return <span className="text-xs text-slate-400">—</span>;
  const color = { sky: "text-sky-800", emerald: "text-emerald-800", slate: "text-slate-800" }[tint] || "text-slate-800";
  const dot = { sky: "bg-sky-400", emerald: "bg-emerald-400", slate: "bg-slate-400" }[tint] || "bg-slate-400";
  const dateStr = at ? new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  return (
    <div className="text-xs">
      <div className={`flex items-center gap-1.5 ${color} font-medium truncate max-w-[130px]`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
        <span className="truncate">{name}</span>
      </div>
      {dateStr && <div className="text-[10px] text-slate-500 font-mono ml-3">{dateStr}</div>}
    </div>
  );
}

function RecipientCell({ p }) {
  const isCrypto = p.payment_mode === "crypto";
  const primary = isCrypto ? p.wallet_address : (p.bank_details?.account_number || p.bank_details?.iban);
  const secondary = isCrypto ? p.network : (p.bank_details?.ifsc || p.bank_details?.swift || p.bank_details?.routing);
  const name = p.bank_details?.account_name;
  if (!primary && !name) return <span className="text-xs text-slate-400">—</span>;
  const copy = (e, val) => {
    e.stopPropagation(); e.preventDefault();
    // Danger prompt for paid/verified stages — user explicitly asked for this
    // to prevent double-payment mistakes from copying an already-settled wallet.
    if (["paid", "verified"].includes(p.stage)) {
      const label = isCrypto ? "wallet address" : "account number";
      const ok = window.confirm(
        `⚠ WARNING · POSSIBLE DOUBLE PAYMENT\n\n` +
        `This request is already ${p.stage.toUpperCase()}.\n\n` +
        `${p.reference} · ${p.brand_name}\n` +
        `${p.currency_code} ${p.net_amount} → ${p.vendor_name}\n\n` +
        `Copying the ${label} may cause you to pay a second time.\n\nContinue?`
      );
      if (!ok) return;
    }
    navigator.clipboard.writeText(val);
    toast.success("Copied");
  };
  return (
    <div className="text-xs">
      {name && <div className="truncate max-w-[180px]">{name}</div>}
      {primary && (
        <div className="flex items-center gap-1 font-mono">
          <span className="truncate max-w-[140px]" title={primary}>{primary}</span>
          <button data-testid={`queue-copy-${p.reference}`} onClick={(e) => copy(e, primary)} className="p-0.5 rounded hover:bg-amber-100 text-slate-500 hover:text-amber-700">
            <Copy size={12} weight="bold" />
          </button>
        </div>
      )}
      {secondary && <div className="font-mono text-slate-500">{secondary}</div>}
    </div>
  );
}


function MobileRequestCard({ p, selected, onToggle, canSwipeApprove = false, onSwipeApprove, onSwipeReject }) {
  const dateStr = new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  // Wallet expiry — same rules as the desktop table
  const expiredMs = p.wallet_expires_at ? Date.parse(p.wallet_expires_at) : null;
  const walletExpired = expiredMs && Number.isFinite(expiredMs) && expiredMs < Date.now()
    && ["draft","pending_approval","pending_audit","approved","rejected"].includes(p.stage);
  // Swipe gesture state — only wired up when this card is actionable by the current user.
  const [dragX, setDragX] = useState(0);
  const [startX, setStartX] = useState(null);
  const REVEAL = 60, COMMIT = 130, MAX = 180;

  const onTouchStart = (e) => {
    if (!canSwipeApprove) return;
    setStartX(e.touches[0].clientX);
  };
  const onTouchMove = (e) => {
    if (startX === null) return;
    const dx = e.touches[0].clientX - startX;
    setDragX(Math.max(-MAX, Math.min(MAX, dx)));
  };
  const onTouchEnd = () => {
    if (startX === null) return;
    setStartX(null);
    if (dragX <= -COMMIT) { setDragX(-MAX); setTimeout(() => { setDragX(0); onSwipeApprove?.(p); }, 150); }
    else if (dragX >= COMMIT) { setDragX(MAX); setTimeout(() => { setDragX(0); onSwipeReject?.(p); }, 150); }
    else setDragX(0);
  };
  const abs = Math.abs(dragX);

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {canSwipeApprove && (
        <>
          <div
            className={`absolute inset-y-0 right-0 flex items-center gap-2 px-5 bg-emerald-500 text-white font-bold uppercase tracking-wider text-xs transition-opacity ${dragX < -REVEAL ? "opacity-100" : "opacity-60"}`}
            style={{ width: `${Math.min(MAX, abs)}px` }}
          >
            <ThumbsUp size={16} weight="fill" />
            {abs >= COMMIT ? "Release" : "Approve"}
          </div>
          <div
            className={`absolute inset-y-0 left-0 flex items-center gap-2 px-5 bg-red-500 text-white font-bold uppercase tracking-wider text-xs transition-opacity ${dragX > REVEAL ? "opacity-100" : "opacity-60"}`}
            style={{ width: `${Math.min(MAX, abs)}px`, flexDirection: "row-reverse" }}
          >
            <XCircle size={16} weight="fill" />
            {abs >= COMMIT ? "Release" : "Reject"}
          </div>
        </>
      )}

      <div
        data-testid={`queue-card-${p.reference}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: startX === null ? "transform 150ms ease-out" : "none",
          touchAction: "pan-y",
        }}
        className={`relative glass rounded-2xl p-4 shimmer-border ${selected ? "ring-2 ring-amber-400 bg-amber-50/50" : ""} ${walletExpired ? "!border-2 !border-red-400 bg-red-50/40" : ""}`}
      >
      {walletExpired && (
        <div data-testid={`queue-card-wallet-expired-${p.reference}`} className="mb-3 -mt-1 rounded-lg bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 flex items-center gap-1.5">
          ⚠ Do NOT pay — wallet {fmtRemaining(p.wallet_expires_at)}
        </div>
      )}
      {!walletExpired && p.wallet_expires_at && (
        <div data-testid={`queue-card-wallet-remaining-${p.reference}`} className="mb-3 -mt-1 rounded-lg bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 flex items-center gap-1.5">
          ⏳ Wallet valid for {fmtRemaining(p.wallet_expires_at)}
        </div>
      )}
      {/* Row 1: checkbox + reference + stage */}
      <div className="flex items-start gap-3 mb-3">
        <input
          data-testid={`bulk-select-card-${p.reference}`}
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 accent-amber-600 cursor-pointer w-4 h-4 shrink-0"
        />
        <Link
          to={`/requests/${p.id}`}
          data-testid={`queue-card-open-${p.reference}`}
          className="flex-1 min-w-0 flex items-center justify-between gap-2"
        >
          <div className="min-w-0">
            <div className="font-mono text-sm text-amber-700 font-bold truncate">{p.reference}</div>
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">{dateStr}</div>
          </div>
          <StageBadge stage={p.stage} approvalStep={p.approval_step} />
        </Link>
      </div>

      {/* Row 2: brand(s) + vendor */}
      <Link to={`/requests/${p.id}`} className="block">
        {p.brand_allocations && p.brand_allocations.length > 1 ? (
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-violet-100 border border-violet-300 text-violet-900">
                {p.brand_allocations.length}-brand split
              </span>
              <span className="text-sm font-semibold text-slate-800 truncate flex-1">{p.vendor_name}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 pl-1">
              {p.brand_allocations.map((a) => (
                <div key={a.brand_id} className="inline-flex items-center gap-1 text-[10px] bg-white/70 rounded-full pl-1.5 pr-2 py-0.5 border border-amber-200">
                  <span className="uppercase tracking-wider font-bold text-amber-900 truncate max-w-[100px]">{a.brand_name}</span>
                  <span className="font-mono text-slate-800">{fmtAmount(a.amount, p.currency_code)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900">
              {p.brand_name || "—"}
            </span>
            <span className="text-sm font-semibold text-slate-800 truncate">{p.vendor_name}</span>
          </div>
        )}

        {/* Row 3: amount + currency + mode */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <CurrencyIcon code={p.currency_code} size={22} />
            <div className="font-mono font-black text-lg text-slate-900 truncate">
              {fmtAmount(p.net_amount, p.currency_code)}
            </div>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
            {p.payment_mode}{p.network ? ` · ${p.network}` : ""}
          </div>
        </div>

        {/* Row 4: expense + billing period */}
        {(p.expense_type || p.billing_month) && (
          <div className="flex items-center gap-2 text-[11px] text-slate-600 mb-3 flex-wrap">
            {p.expense_type && <span className="px-2 py-0.5 rounded-md bg-white/60 border border-white/70">{p.expense_type}</span>}
            {p.billing_month && <span className="font-mono">{fmtBillingPeriod(p.billing_month, p.billing_month_end)}</span>}
          </div>
        )}

        {/* Row 5: actors */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 pt-3 border-t border-white/50 text-[11px]">
          <MiniActor label="req" name={p.requester_name} tint="slate" />
          <MiniActor label="aud" name={p.audited_by_name} tint="sky" />
          <MiniActor label="pay" name={p.paid_by_name || p.assigned_payer_name} tint={p.paid_by_name ? "emerald" : "amber"} />
          {p.accounting_date && (
            <span className="ml-auto font-mono text-[10px] text-slate-500">📒 {p.accounting_date}</span>
          )}
        </div>
        {canSwipeApprove && (
          <div className="text-[10px] text-slate-400 mt-2 italic">
            ← swipe left to approve · swipe right to reject →
          </div>
        )}
      </Link>
      </div>
    </div>
  );
}

function MiniActor({ label, name, tint }) {
  if (!name) return null;
  const color = {
    slate: "text-slate-700",
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  }[tint] || "text-slate-700";
  const dot = {
    slate: "bg-slate-400",
    sky: "bg-sky-400",
    emerald: "bg-emerald-400",
    amber: "bg-amber-400",
  }[tint] || "bg-slate-400";
  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
      <span className="uppercase font-mono text-[9px] text-slate-500">{label}</span>
      <span className="truncate max-w-[90px] font-medium">{name}</span>
    </div>
  );
}
